// « Centralisation » — C5 (PMS-LEDGER-CENTRALISATION-BRIEF §5f): per month, exactly what macompta receives (journal
// CTPMS), and the month's figures read from it: revenue HT per account, VAT collected, taxe de séjour, takings per
// tender. The bank side (remises, fees) is never sent: macompta books it from the SG statement (operator, 2026-10-07). Read-only: the centralisation is issued by the operator's tool
// (centralise_month.py), proven equal to the detail account by account in the database.
//
// Honesty: a missing right is « Droit manquant », never an empty page; a month never centralised says so (an
// answer, not an error); a failed read says the centralisation is unreadable and can be retried.

import { useCallback, useEffect, useState } from 'react';
import {
  centralisationSummary,
  currentMonthParis,
  formatAmount,
  formatDay,
  formatMonthLong,
  isMonth,
  journalLabel,
  shiftMonth,
  sumAmounts,
  type AccountAmount,
  type Centralisation as CentralisationData,
  type CentralisationLine,
  type LedgerReader,
} from '@lemontaigu/fin-core';

function Figures({ title, rows, testId }: { title: string; rows: readonly AccountAmount[]; testId: string }) {
  return (
    <div className="fin-ct-block" data-testid={testId}>
      <h4>{title}</h4>
      {rows.length === 0 ? (
        <p className="muted">—</p>
      ) : (
        <table className="pms-table">
          <tbody>
            {rows.map((r) => (
              <tr key={r.account} data-account={r.account}>
                <td>{r.account}</td>
                <td>{r.label ?? ''}</td>
                <td className="fin-num">{formatAmount(r.amountCents)}</td>
              </tr>
            ))}
          </tbody>
          {rows.length > 1 && (
            <tfoot>
              <tr>
                <th colSpan={2}>Total</th>
                <th className="fin-num">{formatAmount(sumAmounts(rows))}</th>
              </tr>
            </tfoot>
          )}
        </table>
      )}
    </div>
  );
}

/** The entries macompta receives, grouped by journal and piece. */
function Entries({ lines }: { lines: readonly CentralisationLine[] }) {
  const groups = new Map<string, CentralisationLine[]>();
  for (const l of lines) {
    const k = `${l.targetJournal}|${l.piece}|${l.entryDate}`;
    groups.set(k, [...(groups.get(k) ?? []), l]);
  }
  return (
    <div className="pms-table-wrap" data-testid="fin-ct-entries">
      <table className="pms-table">
        <thead>
          <tr>
            <th>Journal</th>
            <th>Date</th>
            <th>Pièce</th>
            <th>Compte</th>
            <th>Libellé</th>
            <th className="fin-num">Débit</th>
            <th className="fin-num">Crédit</th>
          </tr>
        </thead>
        <tbody>
          {[...groups.values()].map((g) =>
            g.map((l, i) => (
              <tr key={l.lineNo} className={i === 0 ? 'fin-ct-first' : undefined}>
                <td>{i === 0 ? journalLabel(l.targetJournal) : ''}</td>
                <td>{i === 0 ? formatDay(l.entryDate) : ''}</td>
                <td>{i === 0 ? l.piece : ''}</td>
                <td>{l.account}</td>
                <td>{l.label}</td>
                <td className="fin-num">{l.debitCents === 0 ? '' : formatAmount(l.debitCents)}</td>
                <td className="fin-num">{l.creditCents === 0 ? '' : formatAmount(l.creditCents)}</td>
              </tr>
            )),
          )}
        </tbody>
      </table>
    </div>
  );
}

export function CentralisationView({ data, month }: { data: CentralisationData | null; month: string }) {
  const [showEntries, setShowEntries] = useState(false);
  if (data === null) {
    return (
      <p className="muted" data-testid="fin-ct-none">
        {formatMonthLong(month)} n’a pas encore été centralisé.
      </p>
    );
  }
  const s = centralisationSummary(data.lines);
  const entries = Object.entries(s.entriesPerJournal)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([j, n]) => `${journalLabel(j)} : ${n}`)
    .join(' · ');
  return (
    <div data-testid="fin-ct">
      <p data-testid="fin-ct-head">
        Version {data.version}, du {formatDay(data.periodStart)} au {formatDay(data.periodEnd)}
        {data.sealed ? ' — période scellée, version définitive' : ' — période ouverte, peut être réémise'}. Écritures reçues par macompta : {entries}.
      </p>
      <div className="fin-ct-grid">
        <Figures title="Chiffre d’affaires HT" rows={s.revenue} testId="fin-ct-revenue" />
        <Figures title="TVA collectée" rows={s.vat} testId="fin-ct-vat" />
        <Figures title="Taxe de séjour collectée" rows={s.touristTax} testId="fin-ct-tax" />
        <Figures title="Encaissements par mode" rows={s.takings} testId="fin-ct-takings" />
      </div>
      {s.debitCents === s.creditCents ? (
        <p className="fin-agree" data-testid="fin-ct-ok">
          Centralisation équilibrée ({formatAmount(s.debitCents)}), égale au détail du livre compte par compte.
        </p>
      ) : (
        <p className="fin-health fin-health--alert" role="alert" data-testid="fin-ct-defect">
          Centralisation déséquilibrée : c’est un défaut, pas une présentation.
        </p>
      )}
      <button type="button" onClick={() => setShowEntries(!showEntries)} data-testid="fin-ct-toggle">
        {showEntries ? 'Masquer les écritures' : `Voir les ${data.lines.length} lignes envoyées à macompta`}
      </button>
      {showEntries && <Entries lines={data.lines} />}
    </div>
  );
}

type State =
  | { phase: 'loading' }
  | { phase: 'ready'; data: CentralisationData | null }
  | { phase: 'forbidden' }
  | { phase: 'error'; message: string };

export function Centralisation({ ledger, book, now }: { ledger: LedgerReader; book: string; now?: Date }) {
  const [month, setMonth] = useState(() => currentMonthParis(now ?? new Date()));
  const [state, setState] = useState<State>({ phase: 'loading' });

  const load = useCallback(async () => {
    setState({ phase: 'loading' });
    const r = await ledger.centralisation(book, month);
    if (r.ok) setState({ phase: 'ready', data: r.value });
    else if (r.error.kind === 'forbidden') setState({ phase: 'forbidden' });
    else setState({ phase: 'error', message: r.error.message });
  }, [ledger, book, month]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="fin-ct-panel" aria-label="Centralisation">
      <h3>Centralisation</h3>
      <div className="fin-month">
        <button type="button" aria-label="Mois précédent" onClick={() => setMonth(shiftMonth(month, -1))}>‹</button>
        <input type="month" aria-label="Mois" value={month} onChange={(e) => isMonth(e.target.value) && setMonth(e.target.value)} />
        <button type="button" aria-label="Mois suivant" onClick={() => setMonth(shiftMonth(month, 1))}>›</button>
        <strong>{formatMonthLong(month)}</strong>
      </div>
      {state.phase === 'loading' && <p className="muted" data-testid="fin-ct-loading">Chargement de la centralisation…</p>}
      {state.phase === 'forbidden' && (
        <div className="pms-warn" role="alert" data-testid="fin-ct-forbidden">
          Droit manquant : la centralisation est réservée à l’administrateur.
        </div>
      )}
      {state.phase === 'error' && (
        <div className="pms-warn" role="alert" data-testid="fin-ct-error">
          <p>La centralisation est illisible : ne rien en conclure. ({state.message})</p>
          <button type="button" onClick={() => void load()}>Réessayer</button>
        </div>
      )}
      {state.phase === 'ready' && <CentralisationView data={state.data} month={month} />}
    </section>
  );
}
